import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const loginUrl = `${process.env.REACT_APP_BASE_URL}token/`;
const refreshApi = `${process.env.REACT_APP_BASE_URL}refresh/`;

const tokenDuration =
  parseInt(process.env.REACT_APP_TOKEN_DURATION, 10) * 60 * 1000; // in milliseconds
const refreshDuration =
  parseInt(process.env.REACT_APP_REFRESH_DURATION, 10) * 60 * 1000; // in milliseconds

// Load initial state from localStorage
const initialState = {
  accessToken: localStorage.getItem("accessToken") || null,
  refreshToken: localStorage.getItem("refreshToken") || null,
  accessExpiration: localStorage.getItem("accessExpiration") || null,
  refreshExpiration: localStorage.getItem("refreshExpiration") || null,
  accessLoading: false,
  accessError: null,
  refreshLoading: false,
  refreshError: null,
  username: localStorage.getItem("userName") || null,
  isLoggedIn: localStorage.getItem("isLoggedIn") || null,
  isStaff: false,
  isActive: false,
  isSuperUser: false,
  isCompany: localStorage.getItem("isCompany") || null,
  userStatusLoading: false,
  userStatusError: false,
};

export const getRefreshToken = createAsyncThunk(
  "login/getRefreshToken",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(loginUrl, {
        username: credentials.username,
        password: credentials.password,
      });

      if (response.data) {
        return response.data;
      } else {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAccessToken = createAsyncThunk(
  "login/refreshToken",
  async (_, { getState, rejectWithValue }) => {
    const state = getState().login;
    try {
      const response = await axios.post(refreshApi, {
        refresh: state.refreshToken, // Fix this reference
      });

      if (response.data) {
        return response.data;
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getUserInfo = createAsyncThunk(
  "login/getUserInfo",
  async (accessToken, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}user_status`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
    } catch (error) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.isStaff = false;
      state.isActive = false;
      state.isSuperUser = false;
      state.accessExpiration = null;
      state.refreshExpiration = null;
      state.userStatusLoading = false;
      state.userStatusError = false;
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userName");
      localStorage.removeItem("isCompany");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessExpiration");
      localStorage.removeItem("refreshExpiration");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRefreshToken.pending, (state) => {
        state.refreshLoading = true;
        state.refreshError = null;
      })
      .addCase(getRefreshToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.access;
        localStorage.setItem("accessToken", action.payload.access);
        state.isLoggedIn = "true";
        localStorage.setItem("isLoggedIn", "true");
        state.refreshToken = action.payload.refresh;
        localStorage.setItem("refreshToken", action.payload.refresh);
        const accessExpiration = Date.now() + tokenDuration;
        state.accessExpiration = accessExpiration;
        localStorage.setItem("accessExpiration", accessExpiration.toString());
        const refreshExpiration = Date.now() + refreshDuration;
        state.refreshExpiration = refreshExpiration;
        localStorage.setItem("refreshExpiration", refreshExpiration.toString());
        state.refreshLoading = false;
      })
      .addCase(getRefreshToken.rejected, (state, action) => {
        state.refreshLoading = false;
        state.refreshError = action.payload;
        state.accessToken = null;
        state.refreshToken = null;
        state.isStaff = false;
        state.isActive = false;
        state.isSuperUser = false;
        state.accessExpiration = null;
        state.refreshExpiration = null;
        state.userStatusLoading = false;
        state.userStatusError = false;
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userName");
        localStorage.removeItem("isCompany");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("accessExpiration");
        localStorage.removeItem("refreshExpiration");
      })
      .addCase(getAccessToken.pending, (state) => {
        state.accessLoading = true;
        state.accessError = null;
      })
      .addCase(getAccessToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.access;
        localStorage.setItem("accessToken", action.payload.access);
        state.isLoggedIn = "true";
        localStorage.setItem("isLoggedIn", "true");
        const accessExpiration = Date.now() + tokenDuration;
        state.accessExpiration = accessExpiration;
        localStorage.setItem("accessExpiration", accessExpiration.toString());
        state.accessLoading = false;
      })
      .addCase(getAccessToken.rejected, (state, action) => {
        state.accessLoading = false;
        state.accessError = action.payload;
        localStorage.removeItem("isLoggedIn");
      })
      .addCase(getUserInfo.pending, (state) => {
        state.userStatusLoading = true;
        state.userStatusError = false;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.isStaff = action.payload.is_staff || false;
        state.isActive = action.payload.is_active || false;
        state.isSuperUser = action.payload.is_superuser || false;
        state.userStatusLoading = false;
        localStorage.setItem("userName", action.payload.username);
        state.username = action.payload.username;
        localStorage.setItem("isCompany", action.payload.iscompany);
        state.isCompany = action.payload.iscompany;
        localStorage.setItem("isLoggedIn", action.payload.iscompany);
        state.isLoggedIn = "true";
      })
      .addCase(getUserInfo.rejected, (state) => {
        state.userStatusLoading = false;
        state.userStatusError = true;
      });
  },
});

export const { logoutUser } = loginSlice.actions;
export default loginSlice.reducer;
